import type { TownOfficeObject } from "@town/contracts";
import { createPixelCanvas, type Sprite } from "./paint";
import { HEIGHT_PIXELS_PER_UNIT, unitsToPixels } from "./units";

/**
 * Static Blender output (assets/blender/generate_sprites.py). An anchor is measured from the crop's top-left to the
 * model's base point: the floor centre of the footprint, raised to `placement.surfaceHeight` for tabletop props.
 */
export type FurnitureSpriteTable = Readonly<{
  version: 1;
  density: 32;
  atlases: readonly Readonly<{ url: string; width: number; height: number }>[];
  sprites: Readonly<Record<string, Readonly<Record<string, Readonly<{
    atlas: number; x: number; y: number; width: number; height: number;
    anchorX: number; anchorY: number;
  }>>>>>;
}>;

/** The atlas table; atlas image URLs inside it are relative to this file. */
export const FURNITURE_SPRITES_URL = "/assets/official/sprites/furniture.json";

export class FurnitureSprites {
  readonly #sprites = new Map<string, Sprite>();
  readonly #listeners = new Set<() => void>();
  #loading: Promise<void> | null = null;

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  /** Crop once per asset/rotation; the scene owns positioning and supported elevation. */
  install(table: FurnitureSpriteTable, images: readonly CanvasImageSource[]): void {
    if (table.version !== 1 || table.density !== 32) throw new Error("Unsupported furniture sprite format");
    const sprites = new Map<string, Sprite>();
    for (const [id, rotations] of Object.entries(table.sprites)) {
      for (const [rotation, rect] of Object.entries(rotations)) {
        const atlas = table.atlases[rect.atlas];
        const image = images[rect.atlas];
        const integers = [rect.atlas, rect.x, rect.y, rect.width, rect.height, rect.anchorX, rect.anchorY];
        if (!id.startsWith("official-") || !["0", "90", "180", "270"].includes(rotation)
          || !atlas || !image || !integers.every(Number.isSafeInteger)
          || rect.x < 0 || rect.y < 0 || rect.width <= 0 || rect.height <= 0
          || rect.x + rect.width > atlas.width || rect.y + rect.height > atlas.height) {
          throw new Error("Invalid furniture sprite rectangle");
        }
        const canvas = createPixelCanvas(rect.width, rect.height);
        const context = canvas.getContext("2d");
        if (!context) throw new Error("2D canvas is unavailable");
        context.imageSmoothingEnabled = false;
        context.drawImage(image, rect.x, rect.y, rect.width, rect.height, 0, 0, rect.width, rect.height);
        sprites.set(`${id}:${rotation}`, { canvas, width: rect.width, height: rect.height, anchorX: rect.anchorX, anchorY: rect.anchorY });
      }
    }
    this.#sprites.clear();
    for (const [key, sprite] of sprites) this.#sprites.set(key, sprite);
    for (const listener of this.#listeners) listener();
  }

  /** A crop by catalog id, anchored at its footprint centre. The landing preview places props without office objects. */
  byId(id: string, rotation: 0 | 90 | 180 | 270 = 0): Sprite | null {
    return this.#sprites.get(`${id}:${rotation}`) ?? null;
  }

  get(object: TownOfficeObject): Sprite | null {
    const sprite = this.#sprites.get(`${object.asset?.id}:${object.rotation}`);
    if (!sprite) return null;
    const quarter = object.rotation === 90 || object.rotation === 270;
    const width = quarter ? object.footprint.depth : object.footprint.width;
    const depth = quarter ? object.footprint.width : object.footprint.depth;
    // WorldEntity places furniture from its footprint minimum; the atlas anchors at the footprint centre.
    // A tabletop prop's base point is baked at its authored surface height; the support's own elevation
    // (surfaceHeight + supportElevation, 20 rows per tile) lifts the anchor onto the actual table.
    const elevation = (object.placement?.surfaceHeight ?? 0) + (object.supportElevation ?? 0);
    return { ...sprite, anchorX: sprite.anchorX - unitsToPixels(width / 2),
      anchorY: sprite.anchorY - unitsToPixels(depth / 2) + Math.round(elevation * HEIGHT_PIXELS_PER_UNIT) };
  }

  /** A shared, immutable asset load; absent atlases retain the procedural fallback. */
  load(url = FURNITURE_SPRITES_URL): Promise<void> {
    return this.#loading ??= this.#load(url).catch(() => {});
  }

  async #load(url: string): Promise<void> {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Furniture sprites are unavailable");
    const table: FurnitureSpriteTable = await response.json();
    const images = await Promise.all(table.atlases.map(async atlas => {
      const image = new Image();
      image.src = new URL(atlas.url, new URL(url, location.href)).href;
      await image.decode();
      if (image.naturalWidth !== atlas.width || image.naturalHeight !== atlas.height) throw new Error("Furniture atlas size differs from its table");
      return image;
    }));
    this.install(table, images);
  }
}

export const furnitureSprites = new FurnitureSprites();
